import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = [
    "itemRow",
    "attachmentModal", "attachmentList", "attachmentSaveBtn", "uploadChips", "notifyToggle", "notifyCheckboxes", "notifySelectAll", "notifySelectAllWrap", "uploadDropzone", "fileInput",
    "cscModal", "cscLockedBanner", "cscLockedState", "cscLockedBy",
    "cscOwner", "cscContractor", "cscConsultant", "cscProjectName",
    "cscAgreementDate", "cscProjectNumber",
    "cscCertDate", "cscCertDateDisplay",
    "cscQ2", "cscQ2Display", "cscQ3", "cscQ3Display",
    "cscQ4", "cscQ4Display", "cscAcceptsDate", "cscAcceptsDateDisplay",
    "cscQ5", "cscQ5Display",
    "sig1Row", "sig1Text", "sig1Title", "sig1Date",
    "sig2Row", "sig2Text", "sig2Title", "sig2Date",
    "sig3Row", "sig3Text", "sig3Title", "sig3Date",
    "cscHistoryLog", "cscHistoryBody",
    "cscSaveBtn", "cscCompleteBtn",
    "signModal", "signTitle", "signPreview",
    "mustAddFilesModal",
    "itemHistoryModal", "itemHistoryList", "itemHistoryEmpty", "itemHistoryLoading", "itemHistoryItemName",
    "confirmUnsignModal",
    "confirmCompleteModal"
  ]

  static values = {
    projectId: Number,
    currentUserId: Number,
    isOwner: Boolean,
    isConsultant: Boolean,
    isContractor: Boolean,
    isStakeholder: Boolean,
    presignUrl: String,
    registerUrl: String,
    notifyMembersUrl: String,
    currentUserRole: String,
    currentUserEmail: String,
    currentUserCompany: String
  }

  connect() {
    this._csrfToken = document.querySelector('meta[name="csrf-token"]')?.content
    this._activeAttachmentRow = null
    this._pendingUploads = []
    this._existingAttachmentIds = []
    this._signingType = null

    // Apply role-based checkbox restrictions
    this._applyRoleRestrictions()
  }

  // ─── Role-based restrictions ───────────────────────────

  _applyRoleRestrictions() {
    this.itemRowTargets.forEach(row => {
      const checkboxes = row.querySelectorAll('input[type="checkbox"][data-role]')
      checkboxes.forEach(cb => {
        const role = cb.dataset.role
        if (role === "na") return // N/A handled separately

        if (this.isContractorValue && !this.isOwnerValue && !this.isConsultantValue) {
          // Contractor: only GC column
          if (role !== "gc") cb.disabled = true
        } else if (this.isConsultantValue && !this.isOwnerValue) {
          // Consultant: only PC column
          if (role !== "pc") cb.disabled = true
        } else if (this.isStakeholderValue && !this.isOwnerValue && !this.isConsultantValue && !this.isContractorValue) {
          // Stakeholder: only FM and AA columns
          if (role !== "fm" && role !== "aa") cb.disabled = true
        }
        // Owner: all columns enabled (no restriction)
      })
    })
  }

  // ─── Checkbox toggling ─────────────────────────────────

  toggleCheckbox(event) {
    const cb = event.target
    const row = cb.closest("[data-sequence]")
    const seq = row.dataset.sequence
    const role = cb.dataset.role
    const checked = cb.checked
    const requiresFiles = row.dataset.requiresFiles === "true"

    // Check if files required and none attached
    if (checked && requiresFiles) {
      const attData = JSON.parse(row.dataset.attachmentData || "[]")
      if (attData.length === 0) {
        cb.checked = false
        this._showMustAddFiles()
        return
      }
    }

    const endpoint = `/projects/${this.projectIdValue}/closeout/${role}_checkbox_update`
    this._post(endpoint, { sequence: seq, checked: checked.toString() })
      .then(res => {
        if (res.ok) {
          this._showToast("Saved.")
          return res.json()
        } else {
          this._showToast("Save failed.", "alert")
          cb.checked = !checked
          return null
        }
      })
      .then(data => {
        if (data) {
          row.dataset.completed = data.completed.toString()
          this._updateProgressDisplay(data.progress_label)
          this._paintHistoryCount(row, data.history_count)
        }
      })
      .catch(() => {
        this._showToast("Save failed.", "alert")
        cb.checked = !checked
      })
  }

  toggleNa(event) {
    const cb = event.target
    const row = cb.closest("[data-sequence]")
    const seq = row.dataset.sequence
    const checked = cb.checked

    this._post(`/projects/${this.projectIdValue}/closeout/na_checkbox_update`, {
      sequence: seq, checked: checked.toString()
    }).then(res => {
      if (!res.ok) {
        this._showToast("Save failed.", "alert")
        cb.checked = !checked
        return Promise.reject()
      }
      this._showToast("Saved.")
      return res.json()
    }).then(data => {
      if (!data) return
      row.dataset.completed = data.completed.toString()
      row.dataset.na = checked.toString()
      this._paintHistoryCount(row, data.history_count)
      // Toggle N/A visual state
      if (checked) {
        row.classList.add("opacity-50")
        row.querySelectorAll('input[type="checkbox"][data-role]:not([data-role="na"])').forEach(c => {
          c.disabled = true
        })
        const nameCell = row.querySelector("td:nth-child(5)")
        if (nameCell) {
          nameCell.classList.add("line-through", "text-gray-400")
        }
      } else {
        row.classList.remove("opacity-50")
        this._applyRoleRestrictions()
        const nameCell = row.querySelector("td:nth-child(5)")
        if (nameCell) {
          nameCell.classList.remove("line-through", "text-gray-400")
        }
      }
      this._updateProgressDisplay(data.progress_label)
    }).catch(() => {})
  }

  // ─── Notes ─────────────────────────────────────────────

  saveNote(event) {
    const span = event.target
    const row = span.closest("[data-sequence]")
    if (!row) return
    const seq = row.dataset.sequence
    const notes = span.textContent.trim()

    this._post(`/projects/${this.projectIdValue}/closeout/note_update`, {
      sequence: seq, notes: notes
    }).then(async res => {
      if (res.ok) {
        this._showToast("Note saved.")
        this._paintHistoryCount(row, (await res.json().catch(() => ({}))).history_count)
      } else {
        this._showToast("Failed to save note.", "alert")
      }
    }).catch(() => {
      this._showToast("Failed to save note.", "alert")
    })
  }

  // ─── Attachments ───────────────────────────────────────

  openAttachments(event) {
    const row = event.target.closest("[data-sequence]")
    if (!row) return
    this._activeAttachmentRow = row
    this._pendingUploads = []
    this._existingAttachmentIds = []

    const attData = JSON.parse(row.dataset.attachmentData || "[]")
    this._existingAttachmentIds = attData.map(a => a.id)

    // Render existing attachments as inline chips
    const list = this.attachmentListTarget
    list.innerHTML = ""
    attData.forEach(att => {
      // Anyone on the project can take a file off the row, not just whoever
      // uploaded it — the line's history records who did it, and the removed
      // file stays reachable from there.
      const chip = this._createFileChip(att.name, { removable: true, url: att.url, attachmentId: att.id })
      chip.dataset.attachmentId = att.id

      chip.querySelector("[data-remove-chip]").addEventListener("click", (e) => {
        e.stopPropagation()
        chip.remove()
        this._existingAttachmentIds = this._existingAttachmentIds.filter(id => id !== att.id)
      })

      list.appendChild(chip)
    })

    // Clear upload chips
    this.uploadChipsTarget.innerHTML = ""

    // Reset notify section
    if (this.hasNotifyToggleTarget) this.notifyToggleTarget.checked = false
    this.notifyCheckboxesTarget.classList.add("hidden")
    if (this.hasNotifySelectAllWrapTarget) this.notifySelectAllWrapTarget.classList.add("hidden")
    if (this.hasNotifySelectAllTarget) this.notifySelectAllTarget.checked = false

    this.attachmentModalTarget.classList.remove("hidden")
    document.body.classList.add("overflow-hidden")
  }

  closeAttachmentModal() {
    this.attachmentModalTarget.classList.add("hidden")
    document.body.classList.remove("overflow-hidden")
    this._activeAttachmentRow = null
  }

  toggleNotifySection(event) {
    const checked = event.currentTarget.checked
    if (checked) {
      this.notifyCheckboxesTarget.classList.remove("hidden")
      if (this.hasNotifySelectAllWrapTarget) this.notifySelectAllWrapTarget.classList.remove("hidden")
      this._loadNotifyMembers()
    } else {
      this.notifyCheckboxesTarget.classList.add("hidden")
      if (this.hasNotifySelectAllWrapTarget) this.notifySelectAllWrapTarget.classList.add("hidden")
      // Uncheck all member checkboxes
      this.notifyCheckboxesTarget.querySelectorAll('input[type="checkbox"][data-email]').forEach(cb => { cb.checked = false })
      if (this.hasNotifySelectAllTarget) this.notifySelectAllTarget.checked = false
    }
  }

  notifyToggleAll(event) {
    const checked = event.currentTarget.checked
    this.notifyCheckboxesTarget.querySelectorAll('input[type="checkbox"][data-email]').forEach(cb => {
      cb.checked = checked
    })
  }

  async _loadNotifyMembers() {
    // Reset select all
    if (this.hasNotifySelectAllTarget) this.notifySelectAllTarget.checked = false

    if (this._notifyMembersCache) {
      this._renderNotifyMembers(this._notifyMembersCache)
      return
    }

    // Show spinner
    this.notifyCheckboxesTarget.innerHTML = `<div class="flex justify-center py-4"><div class="w-5 h-5 border-2 border-gray-300 border-t-brand rounded-full animate-spin"></div></div>`

    try {
      const res = await fetch(this.notifyMembersUrlValue, {
        headers: { "Accept": "application/json", "X-CSRF-Token": this._csrfToken },
        credentials: "same-origin"
      })
      if (!res.ok) throw new Error(`HTTP ${res.status}`)
      const data = await res.json()
      this._notifyMembersCache = data.groups
      this._renderNotifyMembers(data.groups)
    } catch (e) {
      console.error("[Closeout] Failed to load notify members:", e)
      this.notifyCheckboxesTarget.innerHTML = `<p class="text-[13px] text-gray-400 py-2">Failed to load members.</p>`
    }
  }

  _renderNotifyMembers(groups) {
    if (!groups || groups.length === 0) {
      this.notifyCheckboxesTarget.innerHTML = `<p class="text-[13px] text-gray-400 py-2">No members found.</p>`
      return
    }

    let html = ""
    groups.forEach(group => {
      html += `<div class="mb-3">
        <div class="flex items-center gap-2 mb-1.5">
          <span class="text-[13px] font-bold uppercase tracking-wide text-gray-400">${this._escapeHtml(group.role)}</span>
          <span class="text-[13px] text-gray-400">&middot;</span>
          <span class="text-[13px] font-medium text-gray-500">${this._escapeHtml(group.company_name)}</span>
        </div>
        <div class="space-y-1">`

      group.members.forEach(member => {
        html += `<label class="flex items-center gap-2.5 px-2 py-1.5 rounded-lg hover:bg-gray-50 cursor-pointer transition-colors">
            <input type="checkbox" data-email="${this._escapeHtml(member.email)}" class="form-checkbox rounded shrink-0" style="accent-color: var(--color-brand)">
            <div class="min-w-0 flex-1 flex items-center gap-2">
              <span class="text-[13px] text-gray-700 truncate">${this._escapeHtml(member.name)}</span>
              <span class="text-[13px] text-gray-400 truncate">${this._escapeHtml(member.email)}</span>
            </div>
          </label>`
      })

      html += `</div></div>`
    })

    this.notifyCheckboxesTarget.innerHTML = html
  }

  triggerFileInput() {
    if (this.hasFileInputTarget) this.fileInputTarget.click()
  }

  dragOver(event) {
    event.preventDefault()
    event.currentTarget.classList.add("border-brand", "bg-brand/5")
  }

  dragLeave(event) {
    event.currentTarget.classList.remove("border-brand", "bg-brand/5")
  }

  dropFiles(event) {
    event.preventDefault()
    event.currentTarget.classList.remove("border-brand", "bg-brand/5")
    this._uploadFiles(event.dataTransfer.files)
  }

  selectFiles(event) {
    this._uploadFiles(event.target.files)
    event.target.value = ""
  }

  async _uploadFiles(fileList) {
    if (!fileList || fileList.length === 0) return

    const files = Array.from(fileList)
    const filesMeta = files.map(f => ({
      filename: f.name,
      content_type: f.type || "application/octet-stream",
      byte_size: f.size
    }))

    // Create upload chips
    const chips = files.map(f => {
      const chip = this._createFileChip(f.name, { uploading: true })
      this.uploadChipsTarget.appendChild(chip)
      return chip
    })

    // Presign
    let presignData
    try {
      const res = await this._post(this.presignUrlValue, { files: filesMeta })
      presignData = await res.json()
    } catch (e) {
      chips.forEach(c => c.remove())
      return
    }

    // Validate presign response
    if (!presignData?.uploads || !Array.isArray(presignData.uploads)) {
      chips.forEach(c => c.remove())
      return
    }

    // Upload to S3 in parallel, tracking success/failure per file
    const uploads = presignData.uploads
    const results = await Promise.all(uploads.map(async (upload, i) => {
      try {
        const res = await fetch(upload.upload_url, {
          method: "PUT",
          body: files[i],
          headers: { "Content-Type": files[i].type || "application/octet-stream" }
        })
        return res.ok
      } catch (e) {
        console.error("S3 upload failed:", e)
        return false
      }
    }))

    // Remove chips for failed uploads
    results.forEach((ok, i) => { if (!ok) chips[i].remove() })

    // Only register successful uploads
    const successfulUploads = uploads.filter((_, i) => results[i])
    const successfulChips = chips.filter((_, i) => results[i])

    if (successfulUploads.length === 0) return

    const registerFiles = successfulUploads.map(u => ({
      cache_name: u.cache_name,
      filename: u.filename
    }))

    try {
      const regRes = await this._post(this.registerUrlValue, { files: registerFiles })
      const registered = await regRes.json()

      registered.forEach((att, i) => {
        const name = att.filename || successfulUploads[i].filename
        this._pendingUploads.push({ id: att.id, name: name, user_id: this.currentUserIdValue, url: att.url })
        // Replace upload chip with removable done chip
        this._updateChipToDone(successfulChips[i], name, att.id, att.url)
      })
    } catch (e) {
      console.error("Register failed:", e)
      successfulChips.forEach(c => c.remove())
    }
  }

  _createFileChip(name, { removable = false, uploading = false, url = null, attachmentId = null } = {}) {
    const chip = document.createElement("div")
    const displayName = name.length > 28 ? name.substring(0, 24) + "..." + name.split(".").pop() : name
    const clickable = !uploading

    chip.className = `inline-flex items-center gap-1.5 pl-2 ${removable ? 'pr-1' : 'pr-2'} py-1 rounded-md text-[13px] ${uploading ? 'bg-blue-50 text-blue-400' : 'bg-gray-100 text-gray-700'} ${clickable ? 'cursor-pointer hover:bg-gray-200 transition-colors' : ''}`

    const icon = uploading
      ? `<svg class="w-3.5 h-3.5 shrink-0 animate-spin" fill="none" viewBox="0 0 24 24"><circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle><path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"></path></svg>`
      : `<svg class="w-3.5 h-3.5 shrink-0 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="1.5"><path stroke-linecap="round" stroke-linejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m2.25 0H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z"/></svg>`

    const removeBtn = removable
      ? `<button type="button" class="ml-0.5 p-0.5 rounded hover:bg-gray-200 text-gray-400 hover:text-gray-600 transition-colors" data-remove-chip><svg class="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2"><path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12"/></svg></button>`
      : ""

    chip.innerHTML = `${icon}<span class="truncate max-w-[180px]" title="${this._escapeHtml(name)}">${this._escapeHtml(displayName)}</span>${removeBtn}`

    if (clickable && url) {
      chip.addEventListener("click", (e) => {
        if (e.target.closest("[data-remove-chip]")) return
        window.open(url, "_blank")
      })
    }

    return chip
  }

  _updateChipToDone(chip, name, attachmentId, url) {
    const newChip = this._createFileChip(name, { removable: true, url: url, attachmentId: attachmentId })
    newChip.querySelector("[data-remove-chip]").addEventListener("click", (e) => {
      e.stopPropagation()
      newChip.remove()
      this._pendingUploads = this._pendingUploads.filter(u => u.id !== attachmentId)
    })
    chip.replaceWith(newChip)
  }

  async saveAttachments() {
    if (!this._activeAttachmentRow) return

    const btn = this.hasAttachmentSaveBtnTarget ? this.attachmentSaveBtnTarget : null
    if (btn) {
      btn.disabled = true
      btn.innerHTML = '<svg class="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24"><circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle><path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"></path></svg>'
    }

    const seq = this._activeAttachmentRow.dataset.sequence
    const pendingIds = this._pendingUploads.map(u => u.id)
    const allIds = [...this._existingAttachmentIds, ...pendingIds]

    // Collect notification emails
    const emailCheckboxes = this.notifyCheckboxesTarget.querySelectorAll('input[type="checkbox"][data-email]:checked')
    const emails = Array.from(emailCheckboxes).map(cb => cb.dataset.email).filter(Boolean)

    const sectionName = this._getSectionName(parseInt(seq))

    try {
      const saveResponse = await this._post(`/projects/${this.projectIdValue}/closeout/update_attachments`, {
        sequence: seq,
        file_ids: allIds.join(" "),
        project_id: this.projectIdValue,
        section_name: sectionName,
        emails_to_notify: emails.length > 0 ? JSON.stringify(emails) : null
      })

      // Rebuild attachment-data on the row so next open shows correct chips
      const existingData = JSON.parse(this._activeAttachmentRow.dataset.attachmentData || "[]")
      const keptExisting = existingData.filter(a => this._existingAttachmentIds.includes(a.id))
      const newData = [...keptExisting, ...this._pendingUploads]
      this._activeAttachmentRow.dataset.attachmentData = JSON.stringify(newData)

      this._paintAttachmentIcon(this._activeAttachmentRow, allIds.length > 0)
      this._paintHistoryCount(this._activeAttachmentRow,
                              (await saveResponse.json().catch(() => ({}))).history_count)

      this.closeAttachmentModal()
      this._showToast("Attachments saved successfully.")
    } finally {
      if (btn) {
        btn.disabled = false
        btn.innerHTML = "Save"
      }
    }
  }

  // Paperclip once the row holds files, plus sign when it's empty — matching
  // what the page renders on load. Taking the last file off used to leave the
  // paperclip behind until a refresh. Works for section headers too, where
  // the cell is a `th`.
  // The badge beside the history icon. Hidden rather than removed at zero so
  // the icons stay in a straight line down the column.
  _paintHistoryCount(row, count) {
    const badge = row?.querySelector(".js-history-count")
    if (!badge || count == null) return

    badge.textContent = count
    badge.classList.toggle("invisible", !(count > 0))
  }

  _paintAttachmentIcon(row, hasFiles) {
    const cell = row.querySelector("td[data-action], th[data-action]")
    if (!cell) return

    cell.innerHTML = hasFiles
      ? `<svg class="w-4 h-4 mx-auto text-blue-500" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" d="m18.375 12.739-7.693 7.693a4.5 4.5 0 0 1-6.364-6.364l10.94-10.94A3 3 0 1 1 19.5 7.372L8.552 18.32m.009-.01-.01.01m5.699-9.941-7.81 7.81a1.5 1.5 0 0 0 2.112 2.13" /></svg>`
      : `<svg class="w-4 h-4 mx-auto text-gray-400" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" d="M12 4.5v15m7.5-7.5h-15" /></svg>`
  }

  // ─── CSC Modal ─────────────────────────────────────────

  downloadDedication(event) {
    event.preventDefault()
    const a1 = document.createElement("a")
    a1.href = "/05_Dedication_Application_Letter.doc"
    a1.download = ""
    a1.click()
    const a2 = document.createElement("a")
    a2.href = "/05a_Dedication_Application.pdf"
    a2.download = ""
    a2.click()
  }

  openCscModal(event) {
    event.preventDefault()
    this.cscModalTarget.classList.remove("hidden")
  }

  closeCscModal() {
    this.cscModalTarget.classList.add("hidden")
  }

  updateCertDateDisplay() {
    if (this.hasCscCertDateTarget && this.hasCscCertDateDisplayTarget) {
      this.cscCertDateDisplayTarget.textContent = this._formatDateForDisplay(this.cscCertDateTarget.value) || "___________"
    }
  }

  updateQ2Display() {
    if (this.hasCscQ2Target && this.hasCscQ2DisplayTarget) {
      this.cscQ2DisplayTarget.textContent = this.cscQ2Target.textContent || "_____"
    }
  }

  updateQ3Display() {
    if (this.hasCscQ3Target && this.hasCscQ3DisplayTarget) {
      const val = this.cscQ3Target.textContent.replace(/[^0-9]/g, "")
      this.cscQ3DisplayTarget.textContent = val ? this._formatNumber(val) : "________"
    }
  }

  updateQ4Display() {
    if (this.hasCscQ4Target && this.hasCscQ4DisplayTarget) {
      const val = this.cscQ4Target.textContent.replace(/[^0-9]/g, "")
      this.cscQ4DisplayTarget.textContent = val ? this._formatNumber(val) : "________"
    }
  }

  updateAcceptsDateDisplay() {
    if (this.hasCscAcceptsDateTarget && this.hasCscAcceptsDateDisplayTarget) {
      this.cscAcceptsDateDisplayTarget.textContent = this._formatDateForDisplay(this.cscAcceptsDateTarget.value) || "_________"
    }
  }

  updateQ5Display() {
    if (this.hasCscQ5Target && this.hasCscQ5DisplayTarget) {
      this.cscQ5DisplayTarget.textContent = this.cscQ5Target.textContent || "_____"
    }
  }

  _collectCscData() {
    const data = {}
    if (this.hasCscOwnerTarget) data.owner_name = this.cscOwnerTarget.textContent.trim()
    if (this.hasCscContractorTarget) data.contractor_name = this.cscContractorTarget.textContent.trim()
    if (this.hasCscConsultantTarget) data.consultant_name = this.cscConsultantTarget.textContent.trim()
    if (this.hasCscProjectNameTarget) data.project_name = this.cscProjectNameTarget.textContent.trim()
    if (this.hasCscProjectNumberTarget) data.project_number = this.cscProjectNumberTarget.textContent.trim()
    if (this.hasCscAgreementDateTarget) data.agreement_date = this.cscAgreementDateTarget.value
    if (this.hasCscCertDateTarget) data.cert_date = this.cscCertDateTarget.value
    if (this.hasCscQ2Target) data.question2 = this.cscQ2Target.textContent.trim()
    if (this.hasCscQ3Target) data.question3 = this.cscQ3Target.textContent.trim().replace(/,/g, "")
    if (this.hasCscQ4Target) data.question4 = this.cscQ4Target.textContent.trim().replace(/,/g, "")
    if (this.hasCscAcceptsDateTarget) data.accepts_date = this.cscAcceptsDateTarget.value
    if (this.hasCscQ5Target) data.question5 = this.cscQ5Target.textContent.trim()

    // Signatures
    if (this.hasSig1TextTarget) data.sig1 = this.sig1TextTarget.textContent.trim()
    if (this.hasSig1TitleTarget) data.sig1_title = this.sig1TitleTarget.textContent.trim()
    if (this.hasSig1DateTarget) data.sig1_date = this.sig1DateTarget.textContent.trim()
    if (this.hasSig2TextTarget) data.sig2 = this.sig2TextTarget.textContent.trim()
    if (this.hasSig2TitleTarget) data.sig2_title = this.sig2TitleTarget.textContent.trim()
    if (this.hasSig2DateTarget) data.sig2_date = this.sig2DateTarget.textContent.trim()
    if (this.hasSig3TextTarget) data.sig3 = this.sig3TextTarget.textContent.trim()
    if (this.hasSig3TitleTarget) data.sig3_title = this.sig3TitleTarget.textContent.trim()
    if (this.hasSig3DateTarget) data.sig3_date = this.sig3DateTarget.textContent.trim()

    return data
  }

  async saveCscDraft() {
    const btn = this.hasCscSaveBtnTarget ? this.cscSaveBtnTarget : null
    if (btn) {
      btn.disabled = true
      btn.dataset.originalText = btn.textContent
      btn.textContent = "Saving..."
    }

    const data = this._collectCscData()
    try {
      const res = await this._post(`/projects/${this.projectIdValue}/closeout/save_csc_draft`, {
        data: JSON.stringify(data)
      })
      const result = await res.json()
      if (res.ok) {
        this._updateCscLockState(result.locked, result.locked_by)
        this._updateCscHistory(result.history_log)
        this.closeCscModal()
        this._showToast("Draft saved successfully.")
      } else {
        this._showToast(result.error || "Save failed", "alert")
      }
    } catch (e) {
      console.error("CSC save failed:", e)
    } finally {
      if (btn) {
        btn.disabled = false
        btn.textContent = btn.dataset.originalText || "Save Draft"
      }
    }
  }

  completeCscPdf() {
    const data = this._collectCscData()

    // Validate all fields are filled (except signatures)
    const requiredFields = [
      ["owner_name", "Owner"], ["contractor_name", "Contractor"], ["consultant_name", "Architect/Consultant"],
      ["project_name", "Project Name"], ["project_number", "Project Number"], ["agreement_date", "Agreement Date"],
      ["cert_date", "Date of Substantial Completion"], ["question2", "Completion Days"],
      ["question3", "Liquidated Damages Amount"], ["question4", "Withhold Amount"],
      ["accepts_date", "Possession Date"], ["question5", "Possession Time"]
    ]
    const empty = requiredFields.filter(([key]) => !data[key] || data[key].trim() === "")
    if (empty.length > 0) {
      this._showToast("All fields need to be filled in to be able to save (apart from signatures).", "alert")
      return
    }

    this.confirmCompleteModalTarget.classList.remove("hidden")
  }

  cancelComplete() {
    this.confirmCompleteModalTarget.classList.add("hidden")
  }

  async confirmComplete() {
    this.confirmCompleteModalTarget.classList.add("hidden")
    const data = this._collectCscData()

    const completeBtn = this.hasCscCompleteBtnTarget ? this.cscCompleteBtnTarget : null
    const saveBtn = this.hasCscSaveBtnTarget ? this.cscSaveBtnTarget : null
    if (completeBtn) {
      completeBtn.disabled = true
      completeBtn.dataset.originalHtml = completeBtn.innerHTML
      completeBtn.innerHTML = '<svg class="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24"><circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle><path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"></path></svg> Generating...'
    }
    if (saveBtn) saveBtn.disabled = true

    try {
      const res = await this._post(`/projects/${this.projectIdValue}/closeout/complete_csc_draft`, {
        data: JSON.stringify(data),
        project_id: this.projectIdValue
      })
      if (res.ok) {
        const result = await res.json()
        if (result.attachments) {
          this._updateRowAttachments(12, result.attachments)
        }
        this.closeCscModal()
        this._showToast("Certificate of Substantial Completion PDF generated successfully.")
      } else {
        const result = await res.json()
        this._showToast(result.error || "PDF generation failed", "alert")
      }
    } catch (e) {
      console.error("CSC complete failed:", e)
    } finally {
      if (completeBtn) {
        completeBtn.disabled = false
        completeBtn.innerHTML = completeBtn.dataset.originalHtml || "Complete &amp; Save PDF"
      }
      if (saveBtn) saveBtn.disabled = false
    }
  }

  // ─── CSC Signatures ───────────────────────────────────

  openSignModal(event) {
    event.preventDefault()
    const signType = event.currentTarget.dataset.signType

    // Role-based signing restrictions
    if (signType === "sig1" && !this.isConsultantValue) {
      this._showToast("Only the Architect/Consultant can sign this row.", "alert")
      return
    }
    if (signType === "sig2" && !this.isContractorValue) {
      this._showToast("Only the Contractor can sign this row.", "alert")
      return
    }
    if (signType === "sig3" && !this.isOwnerValue) {
      this._showToast("Only the Owner can sign this row.", "alert")
      return
    }

    this._signingType = signType
    if (this.hasSignTitleTarget) this.signTitleTarget.value = this.currentUserRoleValue || ""
    this.signModalTarget.classList.remove("hidden")
  }

  closeSignModal() {
    this.signModalTarget.classList.add("hidden")
    this._signingType = null
  }

  async confirmSign() {
    if (!this._signingType) return

    const title = this.hasSignTitleTarget ? this.signTitleTarget.value.trim() : ""
    const sigType = this._signingType
    const sigName = this.hasSignPreviewTarget ? this.signPreviewTarget.textContent.trim() : ""
    const today = new Date().toLocaleDateString("en-US")

    // Build signature-only data
    const data = this._collectCscData()
    data[sigType] = sigName
    data[`${sigType}_title`] = title
    data[`${sigType}_date`] = today

    try {
      const isContractorSigning = this.isContractorValue && !this.isOwnerValue && !this.isConsultantValue
      const res = await this._post(`/projects/${this.projectIdValue}/closeout/save_csc_draft`, {
        data: JSON.stringify(data),
        signature_only: isContractorSigning ? "true" : "false"
      })
      const result = await res.json()
      if (res.ok) {
        // Update user's role with job title
        if (title) {
          this._post(`/projects/${this.projectIdValue}/closeout/update_user_role`, { new_role: title })
        }
        this._updateCscLockState(result.locked, result.locked_by)
        this._updateCscHistory(result.history_log)
        this._updateSignatureRow(sigType, sigName, title, today)
        this.closeSignModal()
        this._showToast("Signed successfully.")
      } else {
        this._showToast(result.error || "Sign failed", "alert")
      }
    } catch (e) {
      console.error("Sign failed:", e)
    }
  }

  unsignCsc(event) {
    this._pendingUnsignType = event.currentTarget.dataset.signType
    this.confirmUnsignModalTarget.classList.remove("hidden")
  }

  cancelUnsign() {
    this._pendingUnsignType = null
    this.confirmUnsignModalTarget.classList.add("hidden")
  }

  async confirmUnsign() {
    const sigType = this._pendingUnsignType
    if (!sigType) return
    this.confirmUnsignModalTarget.classList.add("hidden")

    const data = this._collectCscData()
    data[sigType] = ""
    data[`${sigType}_title`] = ""
    data[`${sigType}_date`] = ""

    try {
      const res = await this._post(`/projects/${this.projectIdValue}/closeout/save_csc_draft`, {
        data: JSON.stringify(data),
        signature_only: "true"
      })
      const result = await res.json()
      if (res.ok) {
        this._updateCscLockState(result.locked, result.locked_by)
        this._updateCscHistory(result.history_log)
        this._clearSignatureRow(sigType)
        this._showToast("Signature removed.")
      } else {
        this._showToast(result.error || "Failed to remove signature.", "alert")
      }
    } catch (e) {
      console.error("Unsign failed:", e)
      this._showToast("Failed to remove signature.", "alert")
    } finally {
      this._pendingUnsignType = null
    }
  }

  _updateCscLockState(locked, lockedBy) {
    if (this.hasCscLockedStateTarget) this.cscLockedStateTarget.value = locked ? "true" : "false"
    if (this.hasCscLockedByTarget) this.cscLockedByTarget.value = lockedBy || ""
    if (this.hasCscLockedBannerTarget) {
      this.cscLockedBannerTarget.classList.toggle("hidden", !locked)
    }
    if (this.hasCscCompleteBtnTarget) {
      this.cscCompleteBtnTarget.disabled = !locked
    }

    const canEdit = (this.isOwnerValue || this.isConsultantValue) && !locked
    const modal = this.hasCscModalTarget ? this.cscModalTarget : null
    if (!modal) return

    // Toggle contenteditable fields
    const editableTargets = [
      this.hasCscOwnerTarget && this.cscOwnerTarget,
      this.hasCscContractorTarget && this.cscContractorTarget,
      this.hasCscConsultantTarget && this.cscConsultantTarget,
      this.hasCscProjectNameTarget && this.cscProjectNameTarget,
      this.hasCscProjectNumberTarget && this.cscProjectNumberTarget,
      this.hasCscQ2Target && this.cscQ2Target,
      this.hasCscQ3Target && this.cscQ3Target,
      this.hasCscQ4Target && this.cscQ4Target,
      this.hasCscQ5Target && this.cscQ5Target
    ].filter(Boolean)

    editableTargets.forEach(el => {
      el.contentEditable = canEdit ? "true" : "false"
      if (canEdit) {
        el.classList.add("bg-amber-50")
      } else {
        el.classList.remove("bg-amber-50")
      }
    })

    // Toggle date inputs
    const dateTargets = [
      this.hasCscAgreementDateTarget && this.cscAgreementDateTarget,
      this.hasCscCertDateTarget && this.cscCertDateTarget,
      this.hasCscAcceptsDateTarget && this.cscAcceptsDateTarget
    ].filter(Boolean)

    dateTargets.forEach(el => {
      el.disabled = !canEdit
      if (canEdit) {
        el.classList.add("bg-amber-50")
        el.classList.remove("bg-gray-100", "bg-white")
      } else {
        el.classList.remove("bg-amber-50")
        el.classList.add("bg-gray-100")
      }
    })
  }

  _updateSignatureRow(sigType, name, title, date) {
    const rowTarget = `${sigType}Row`
    const row = this[`has${rowTarget.charAt(0).toUpperCase() + rowTarget.slice(1)}Target`]
      ? this[`${rowTarget}Target`] : null
    if (!row) return

    // Update signature cell (2nd column) — replace "SIGN HERE" with cursive name
    const sigCell = row.children[1]
    if (sigCell) {
      sigCell.innerHTML = `<span style="font-family: 'Allura', cursive; font-size: 19px;" data-closeout-target="${sigType}Text">${this._escapeHtml(name)}</span>`
    }

    // Update title & date cells
    const titleTarget = `${sigType}Title`
    const dateTarget = `${sigType}Date`
    if (this[`has${titleTarget.charAt(0).toUpperCase() + titleTarget.slice(1)}Target`]) {
      this[`${titleTarget}Target`].textContent = title
    }
    if (this[`has${dateTarget.charAt(0).toUpperCase() + dateTarget.slice(1)}Target`]) {
      this[`${dateTarget}Target`].textContent = date
    }

    // Add × remove button if appropriate
    const canUnsign = (sigType === "sig1" && (this.isConsultantValue || this.isOwnerValue)) ||
                      (sigType === "sig2" && (this.isContractorValue || this.isOwnerValue)) ||
                      (sigType === "sig3" && this.isOwnerValue)
    // Remove existing × if any
    const existing = row.querySelector("[data-action='click->closeout#unsignCsc']")
    if (existing) existing.remove()
    if (canUnsign) {
      const btn = document.createElement("span")
      btn.className = "absolute -right-6 top-1/2 -translate-y-1/2 text-gray-400 hover:text-red-500 cursor-pointer"
      btn.dataset.action = "click->closeout#unsignCsc"
      btn.dataset.signType = sigType
      btn.innerHTML = '<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-4 h-4"><path stroke-linecap="round" stroke-linejoin="round" d="m14.74 9-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 0 1-2.244 2.077H8.084a2.25 2.25 0 0 1-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 0 0-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 0 1 3.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 0 0-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 0 0-7.5 0"/></svg>'
      row.appendChild(btn)
    }
  }

  _clearSignatureRow(sigType) {
    const rowTarget = `${sigType}Row`
    const row = this[`has${rowTarget.charAt(0).toUpperCase() + rowTarget.slice(1)}Target`]
      ? this[`${rowTarget}Target`] : null
    if (!row) return

    // Replace cursive name with "SIGN HERE" button
    const sigCell = row.children[1]
    if (sigCell) {
      sigCell.innerHTML = `<span class="font-medium text-brand cursor-pointer px-2 py-1.5 block" data-action="click->closeout#openSignModal" data-sign-type="${sigType}">SIGN HERE</span>`
    }

    // Clear title & date
    const titleTarget = `${sigType}Title`
    const dateTarget = `${sigType}Date`
    if (this[`has${titleTarget.charAt(0).toUpperCase() + titleTarget.slice(1)}Target`]) {
      this[`${titleTarget}Target`].textContent = ""
    }
    if (this[`has${dateTarget.charAt(0).toUpperCase() + dateTarget.slice(1)}Target`]) {
      this[`${dateTarget}Target`].textContent = ""
    }

    // Remove × button
    const existing = row.querySelector("[data-action='click->closeout#unsignCsc']")
    if (existing) existing.remove()
  }

  _updateRowAttachments(sequence, attachments) {
    const row = this.itemRowTargets.find(r => r.dataset.sequence == sequence)
    if (!row) return

    // Update the data attribute
    row.dataset.attachmentData = JSON.stringify(attachments)

    // Update the paperclip/plus icon cell
    const iconCell = row.querySelector("[data-action='click->closeout#openAttachments']")
    if (iconCell) {
      if (attachments.length > 0) {
        iconCell.innerHTML = '<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-4 h-4 mx-auto text-brand"><path stroke-linecap="round" stroke-linejoin="round" d="m18.375 12.739-7.693 7.693a4.5 4.5 0 0 1-6.364-6.364l10.94-10.94A3 3 0 1 1 19.5 7.372L8.552 18.32m.009-.01-.01.01m5.699-9.941-7.81 7.81a1.5 1.5 0 0 0 2.112 2.13"/></svg>'
      } else {
        iconCell.innerHTML = '<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-4 h-4 mx-auto text-gray-400"><path stroke-linecap="round" stroke-linejoin="round" d="M12 4.5v15m7.5-7.5h-15"/></svg>'
      }
    }
  }

  _updateCscHistory(logs) {
    if (!this.hasCscHistoryBodyTarget || !logs) return
    this.cscHistoryLogTarget.classList.remove("hidden")
    this.cscHistoryBodyTarget.innerHTML = logs.map(log =>
      `<p class="py-0.5">${log.date.split(" ")[0]} - ${this._escapeHtml(log.user_name)} (${this._escapeHtml(log.company_role)}) ${this._escapeHtml(log.message || "")}${log.action_type === "edit" && log.new_value ? ` to '${this._escapeHtml(log.new_value)}'` : ""}</p>`
    ).join("")
  }

  // ─── Must Add Files Modal ─────────────────────────────

  _showMustAddFiles() {
    this.mustAddFilesModalTarget.classList.remove("hidden")
  }

  closeMustAddFilesModal() {
    this.mustAddFilesModalTarget.classList.add("hidden")
  }

  // ─── Line item history ─────────────────────────────────

  openItemHistory(event) {
    event.preventDefault()
    event.stopPropagation()

    // Section headers carry a history too, and they aren't itemRows.
    const row = event.currentTarget.closest("[data-sequence]")
    if (!row) return
    const sequence = row.dataset.sequence

    this.itemHistoryItemNameTarget.textContent = event.currentTarget.dataset.itemName || ""
    this.itemHistoryListTarget.innerHTML = ""
    this.itemHistoryEmptyTarget.classList.add("hidden")
    this.itemHistoryLoadingTarget.classList.remove("hidden")
    this.itemHistoryModalTarget.classList.remove("hidden")

    fetch(`/projects/${this.projectIdValue}/closeout/item_history?sequence=${sequence}`,
          { headers: { Accept: "application/json" } })
      .then(r => r.ok ? r.json() : Promise.reject(r.status))
      .then(data => {
        this.itemHistoryLoadingTarget.classList.add("hidden")
        this._renderItemHistory(data.entries || [])
      })
      .catch(() => {
        this.itemHistoryLoadingTarget.classList.add("hidden")
        this._showToast("Could not load the history.", "alert")
      })
  }

  closeItemHistory() {
    this.itemHistoryModalTarget.classList.add("hidden")
  }

  _renderItemHistory(entries) {
    const list = this.itemHistoryListTarget
    list.innerHTML = ""
    this.itemHistoryEmptyTarget.classList.toggle("hidden", entries.length > 0)

    entries.forEach(entry => {
      const li = document.createElement("li")
      li.className = "flex gap-3 text-[13px]"

      const file = entry.file_name
        ? `<div class="mt-0.5">${entry.file_url
            ? `<a href="${entry.file_url}" target="_blank" class="text-brand hover:underline">${this._escapeHtml(entry.file_name)}</a>`
            : `<span class="text-gray-500">${this._escapeHtml(entry.file_name)}</span>`}</div>`
        : ""

      // The note's new wording is the useful half; the old one is context.
      const note = entry.to
        ? `<div class="mt-0.5 text-gray-600">“${this._escapeHtml(entry.to)}”</div>`
        : ""
      const wasNote = entry.from
        ? `<div class="text-gray-400 line-through">“${this._escapeHtml(entry.from)}”</div>`
        : ""

      li.innerHTML = `
        <div class="shrink-0 w-1.5 h-1.5 mt-[7px] rounded-full bg-gray-300"></div>
        <div class="flex-1 min-w-0">
          <div class="text-gray-800">
            <span class="font-medium">${this._escapeHtml(entry.user)}</span>
            ${this._escapeHtml(entry.text)}
          </div>
          ${file}${wasNote}${note}
          <div class="text-gray-400">${this._escapeHtml(entry.at)}</div>
        </div>
      `
      list.appendChild(li)
    })
  }

  // ─── Helpers ───────────────────────────────────────────

  async _post(url, body) {
    return fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-CSRF-Token": this._csrfToken,
        "Accept": "application/json"
      },
      body: JSON.stringify(body)
    })
  }

  _updateProgressDisplay(label) {
    // Update breadcrumb badge
    const badge = document.querySelector("[data-closeout-progress-badge]")
    if (badge) badge.textContent = label

    // Update sidebar counter
    const sidebar = document.querySelector("[data-closeout-sidebar-count]")
    if (sidebar) sidebar.textContent = label
  }

  _getSectionName(seq) {
    if (seq >= 1 && seq <= 3) return "Pre-Substantial Checklist"
    if (seq >= 4 && seq <= 20) return "Substantial Completion Inspection Checklist"
    if (seq >= 21 && seq <= 26) return "Delivery to Facilities Manager"
    if (seq >= 27 && seq <= 45) return "Final Acceptance Checklist"
    if (seq >= 46 && seq <= 48) return "Internal"
    // 901–905 hold each section's own files (see CloseoutHelper).
    if (seq >= 901 && seq <= 905) {
      return ["Pre-Substantial Checklist", "Substantial Completion Inspection Checklist",
              "Delivery to Facilities Manager", "Final Acceptance Checklist",
              "Internal"][seq - 901]
    }
    return "Closeout"
  }

  _formatNumber(numStr) {
    return parseInt(numStr || "0").toLocaleString("en-US")
  }

  // Dates are stored as DD/MM/YYYY; display inline as MM/DD/YYYY
  _formatDateForDisplay(val) {
    if (!val) return null
    const m = val.match(/^(\d{2})\/(\d{2})\/(\d{4})$/)
    return m ? `${m[2]}/${m[1]}/${m[3]}` : val
  }

  // Safe in BOTH text and attribute position — quotes must be escaped too,
  // or a value carrying an inch mark (`6" VERTICAL CURB`) closes its own
  // attribute and swallows the rest of the tag, data-* attributes included.
  _escapeHtml(str) {
    const div = document.createElement("div")
    div.textContent = str || ""
    return div.innerHTML.replace(/"/g, "&quot;").replace(/'/g, "&#39;")
  }

  _showToast(message, type = "notice") {
    const icons = {
      notice: `<svg class="w-5 h-5 shrink-0" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/></svg>`,
      alert: `<svg class="w-5 h-5 shrink-0" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" d="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z"/></svg>`
    }
    const el = document.createElement("div")
    el.className = `flash-toast-${type}`
    el.setAttribute("data-controller", "flash")
    el.setAttribute("data-flash-dismiss-after-value", "5000")
    el.setAttribute("role", "alert")
    el.innerHTML = `<div class="shrink-0">${icons[type] || icons.notice}</div><p class="flex-1 text-sm">${this._escapeHtml(message)}</p>`
    document.body.appendChild(el)
  }
}
